import { getSupabaseClient } from '@/config';

/** Translator, passed in: these messages are read on the connect form. */
export type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * LINE Ads ad-account validation (client side).
 *
 * ads.line.me sends no CORS headers AND every request must be JWS-signed with
 * the account's secret key, so the browser can't call it directly. This posts to
 * the server proxy /api/line-validate, which signs and calls LINE's
 * /adaccounts/{id}/campaigns server-side. The access + secret keys are the
 * user's own, entered in the connect form.
 */

export interface LineAccountValidation {
  valid: boolean;
  accountName?: string;
  error?: string;
}


/** The caller's session, which the validate proxies require. */
const authHeader = async (): Promise<Record<string, string>> => {
  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function validateLineAdAccount(
  adAccountId: string,
  accessKey: string,
  secretKey: string | undefined,
  t: TFn,
): Promise<LineAccountValidation> {
  try {
    const resp = await fetch('/api/line-validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // The proxy is a signed-in-only route now: it takes a credential and
        // reports whether it works, which is not something to offer the world.
        ...(await authHeader()),
      },
      body: JSON.stringify({
        access_key: accessKey,
        secret_key: secretKey ?? '',
        ad_account_id: adAccountId,
      }),
    });
    const data = (await resp.json().catch(() => null)) as
      | (Partial<LineAccountValidation> & { error?: string })
      | null;
    if (data && typeof data.valid === 'boolean') {
      return { valid: data.valid, accountName: data.accountName, error: data.error };
    }
    return { valid: false, error: data?.error ?? t('settings.connectFailed', { platform: 'LINE Ads' }) };
  } catch {
    return { valid: false, error: t('settings.connectFailed', { platform: 'LINE Ads' }) };
  }
}
